import type {
  PatProEvent,
  ResearchTableArtifact,
} from '../chat/types';

const DEMO_SCENARIO_KEY = 'patpro_dev_demo_scenario';

export type DemoScenarioId =
  | 'happy-path'
  | 'slow-first-response'
  | 'zero-results'
  | 'keyword-degraded'
  | 'partial-stream-failure'
  | 'invalid-citation'
  | 'source-manifest'
  | 'rate-limited'
  | 'very-long-answer';

export type DemoMatterState =
  | 'ready'
  | 'empty'
  | 'unauthorized'
  | 'slow'
  | 'long'
  | 'load-failure'
  | 'create-failure';

export type DemoClaimChartState =
  | 'ready'
  | 'empty'
  | 'unauthorized'
  | 'slow'
  | 'long'
  | 'load-failure'
  | 'create-failure';

export type DemoClaimChartDetailState =
  | 'ready'
  | 'slow'
  | 'empty'
  | 'failed-once'
  | 'unauthorized'
  | 'mutation-failure'
  | 'generation-partial'
  | 'generation-slow'
  | 'long';

export type DemoTabularReviewState =
  | 'ready'
  | 'empty'
  | 'unauthorized'
  | 'slow'
  | 'long'
  | 'scope-mismatch'
  | 'load-failure'
  | 'create-failure';

export type DemoTabularReviewDetailState =
  | 'ready'
  | 'slow'
  | 'empty'
  | 'failed-once'
  | 'unauthorized'
  | 'mutation-failure'
  | 'generation-partial'
  | 'generation-slow'
  | 'long';

export type DemoDocumentUploadState = 'ready' | 'audit';

export type DemoWorkflowState =
  | 'ready'
  | 'slow'
  | 'empty'
  | 'failed-once'
  | 'unauthorized'
  | 'stale'
  | 'mutation-failure'
  | 'mutation-unauthorized'
  | 'mutation-stale'
  | 'launch-failure'
  | 'launch-unauthorized'
  | 'launch-missing'
  | 'launch-partial'
  | 'launch-slow'
  | 'long';

export type DemoScenario = {
  id: DemoScenarioId;
  title: string;
  summary: string;
  category: 'streaming' | 'retrieval' | 'provenance' | 'failure' | 'content';
  expected: string[];
  implemented: boolean;
  /** Machine-checkable wire contract. Tests require these event types in
   *  this order and exactly one terminal event. */
  requiredEventOrder: string[];
  terminalEvent: 'turn_complete' | 'error' | 'http_error';
  httpStatus?: number;
  diagnostic?: {
    retrievalMode: 'nano' | 'keyword_degraded';
    provenancePassed: boolean;
  };
};

export const longContentFixture = {
  url: 'https://records.example.test/prosecution/history/US12345678/office-action/2026-07-23/source/aaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaaa',
  identifier: 'PATPRO-AUDIT-US-12345678-CLAIM-000000000000000000000000000000000000000000000000000000000000000001',
  filename: '2026-07-23_Response_to_Non-Final_Office_Action_US12345678_extremely-long-unbroken-filename-for-overflow-audit.docx',
  tableHeader: 'Record identifier',
  codeMarker: 'const prosecutionRecord',
} as const;

/**
 * Browser-visible inputs for W2.2's development-only upload audit. The
 * fixture uses meaningful filenames because filenames are the user-facing
 * identity throughout the dialog and matter explorer.
 *
 * `retryOnceFilename` completes its deterministic progress timeline, fails
 * once, then succeeds on retry. `existingDuplicateFilename` is already
 * present in the Aurora matter. The long queued filename also exercises
 * narrow-dialog containment without generating a synthetic data URL.
 */
export const documentUploadFixture = {
  successFilename: 'W2.2-allowed-prior-art-reference.pdf',
  retryOnceFilename: 'W2.2-retry-once-processing-failure.docx',
  queuedLongFilename: longContentFixture.filename,
  existingDuplicateFilename: '2026-05-18 Non-Final Office Action.pdf',
  sameBatchDuplicateFilename: 'W2.2-same-batch-duplicate.pdf',
  unsupportedFilename: 'W2.2-unsupported-upload-notes.txt',
  retryFailureMessage:
    'Document processing failed before the file became available. Retry this document.',
  duplicateFailureMessage:
    'A document with this filename is already in the matter.',
  unsupportedFailureMessage:
    'Only DOC, DOCX, and PDF documents can be added to a matter.',
  progressCheckpoints: [18, 46, 73, 100],
  checkpointDelayMs: 180,
} as const;

/**
 * Route-local W3.4 workflow inputs. These names are stable browser-facing
 * identifiers rather than implementation sentinels: semantic tests exercise
 * the same library, editor, and launch surfaces a practitioner uses.
 */
export const workflowFixture = {
  assistantName: 'Examiner interview brief',
  tabularName: 'Product evidence comparison',
  missingDependencyName: 'Unavailable product comparison',
  createdName: 'Enablement evidence checklist',
  editedName: 'Updated examiner interview brief',
  longName: `Workflow-${longContentFixture.identifier}`,
  listDelayMs: 1_200,
  retryDelayMs: 500,
  mutationDelayMs: 650,
  launchDelayMs: 800,
} as const;

const reasoningText =
  'Compare the exact claim language first. Then test whether the record supplies an articulated reason to combine.';
const reasoningContent =
  'The strongest response separates the missing limitation from the rationale to combine.';

export const reasoningTimelineFixture = {
  content: reasoningContent,
  liveEvents: [
    {
      type: 'step',
      id: 'queued',
      label: 'Preparing request',
      status: 'start',
    },
    {
      type: 'step',
      id: 'analysis',
      label: 'Testing the rejection',
      status: 'start',
    },
    { type: 'reasoning', text: 'Compare the exact claim language first. ', isStreaming: true },
    { type: 'reasoning', text: 'Then test whether the record supplies an articulated reason to combine.', isStreaming: true },
    { type: 'content', text: reasoningContent, isStreaming: true },
  ] satisfies PatProEvent[],
  persistedEvents: [
    {
      type: 'step',
      id: 'analysis',
      label: 'Testing the rejection',
      status: 'start',
    },
    {
      type: 'step',
      id: 'analysis',
      label: 'Testing the rejection',
      status: 'complete',
    },
    { type: 'reasoning', text: reasoningText },
    { type: 'content', text: reasoningContent },
  ] satisfies PatProEvent[],
  canonicalEvents: [
    {
      type: 'step',
      id: 'analysis',
      label: 'Testing the rejection',
      status: 'complete',
    },
    { type: 'reasoning', text: reasoningText },
    { type: 'content', text: reasoningContent },
  ] satisfies PatProEvent[],
} as const;

const researchTableArtifact: ResearchTableArtifact = {
  artifact_type: 'research_table',
  rows: [
    {
      type: 'case',
      id: 'case-ksr',
      case_name: 'KSR International Co. v. Teleflex Inc.',
      citation: '550 U.S. 398 (2007)',
      date: '2007-04-30T15:30:00.000Z',
      jurisdiction: 'Supreme Court',
      outcome: 'vacated and remanded',
      doctrines_addressed: ['obviousness', 'motivation to combine'],
      treatment_status: 'good_law',
      similarity: 0.941,
      holding: 'An obviousness analysis must identify an apparent reason to combine known elements.',
    },
    {
      type: 'case',
      id: 'demo-opinion-wands',
      case_name: 'In re Wands',
      citation: '858 F.2d 731 (Fed. Cir. 1988)',
      date: '1988-08-30T20:15:00.000Z',
      jurisdiction: 'Federal Circuit',
      outcome: 'affirmed',
      doctrines_addressed: ['enablement'],
      treatment_status: 'good_law',
      similarity: 0.872,
      holding: 'Enablement depends on whether experimentation is undue.',
    },
    {
      type: 'case',
      id: 'case_spine_4101',
      spine_id: 4101,
      case_name: 'Citation Spine Match v. Example Industries',
      citation: '999 F.4th 101 (Fed. Cir. 2025)',
      date: '2025-02-14T18:45:00.000Z',
      jurisdiction: 'Federal Circuit',
      outcome: 'affirmed',
      doctrines_addressed: ['claim construction'],
      similarity: 0.711,
      holding: `Matched metadata only. Long record token: ${longContentFixture.identifier}`,
    },
    {
      type: 'chunk',
      id: 'mpep-2143',
      title: 'MPEP § 2143',
      source_type: 'mpep',
      similarity: 0.903,
      preview: 'A prima facie case requires an articulated reason supporting the legal conclusion.',
    },
    {
      type: 'chunk',
      id: 'mpep-long-research-table',
      title: `MPEP § 2164.01(a) — ${longContentFixture.identifier}`,
      source_type: 'mpep',
      similarity: 0.684,
      preview: `Long-cell containment fixture ${longContentFixture.url}`,
    },
  ],
  total_cases: 3,
  total_chunks: 2,
  source_types: ['case', 'mpep'],
  query: 'adaptive threshold obviousness',
};

const researchStart: PatProEvent = {
  type: 'step',
  id: 'research_table',
  label: 'Building research table',
  status: 'start',
};
const researchComplete: PatProEvent = {
  type: 'step',
  id: 'research_table',
  label: 'Building research table',
  status: 'complete',
  detail: '5 results',
};
const researchTableEvent: PatProEvent = {
  type: 'research_table',
  payload: researchTableArtifact,
};

export const researchTableTimelineFixture = {
  artifact: researchTableArtifact,
  liveEvents: [
    {
      type: 'step',
      id: 'queued',
      label: 'Preparing request',
      status: 'start',
    },
    researchStart,
    researchTableEvent,
    researchComplete,
  ] satisfies PatProEvent[],
  persistedEvents: [
    researchStart,
    researchTableEvent,
    researchComplete,
  ] satisfies PatProEvent[],
  canonicalEvents: [
    researchComplete,
    researchTableEvent,
  ] satisfies PatProEvent[],
} as const;

export const demoScenarios: DemoScenario[] = [
  {
    id: 'happy-path',
    title: 'Grounded streaming answer',
    summary: 'Normal progress, tool activity, citations-before-prose, and a verified terminal turn.',
    category: 'streaming',
    expected: [
      'Immediate progress acknowledgement',
      'Sources arrive before cited prose',
      'All pending stages close on completion',
    ],
    implemented: true,
    requiredEventOrder: ['step', 'tool_call', 'tool_result', 'thinking', 'citations_ready', 'text_delta', 'turn_complete'],
    terminalEvent: 'turn_complete',
    diagnostic: { retrievalMode: 'nano', provenancePassed: true },
  },
  {
    id: 'slow-first-response',
    title: 'Slow first response',
    summary: 'A deliberately slow acknowledgement exercises the composer and pre-response waiting state.',
    category: 'streaming',
    expected: [
      'Submitted work remains visible',
      'The composer cannot double-submit',
      'Progress begins without layout shift',
    ],
    implemented: true,
    requiredEventOrder: ['step', 'tool_call', 'tool_result', 'citations_ready', 'text_delta', 'turn_complete'],
    terminalEvent: 'turn_complete',
    diagnostic: { retrievalMode: 'nano', provenancePassed: true },
  },
  {
    id: 'zero-results',
    title: 'Retrieval with zero results',
    summary: 'The corpus returns nothing and the answer must clearly distinguish absence of evidence.',
    category: 'retrieval',
    expected: [
      'No verified-source claim is shown',
      'The corpus gap is visible',
      'A useful refinement path remains',
    ],
    implemented: true,
    requiredEventOrder: ['step', 'tool_call', 'tool_result', 'citations_ready', 'text_delta', 'turn_complete'],
    terminalEvent: 'turn_complete',
    diagnostic: { retrievalMode: 'nano', provenancePassed: false },
  },
  {
    id: 'keyword-degraded',
    title: 'Semantic search degraded',
    summary: 'Retrieval succeeds through keyword fallback while semantic search is unavailable.',
    category: 'retrieval',
    expected: [
      'Keyword-only degradation is visible',
      'Available sources remain inspectable',
      'The answer is not presented as fully equivalent',
    ],
    implemented: true,
    requiredEventOrder: ['step', 'tool_call', 'tool_result', 'step', 'citations_ready', 'text_delta', 'turn_complete'],
    terminalEvent: 'turn_complete',
    diagnostic: { retrievalMode: 'keyword_degraded', provenancePassed: true },
  },
  {
    id: 'partial-stream-failure',
    title: 'Partial stream failure',
    summary: 'The connection ends after some prose, preserving partial output with recovery actions.',
    category: 'failure',
    expected: [
      'Partial prose is preserved',
      'Retry and copy actions are available',
      'No progress indicator remains pending',
    ],
    implemented: true,
    requiredEventOrder: ['step', 'tool_call', 'tool_result', 'citations_ready', 'text_delta', 'error'],
    terminalEvent: 'error',
  },
  {
    id: 'invalid-citation',
    title: 'Invalid citation identifier',
    summary: 'The model emits an unmatched citation marker and the provenance gate rejects it.',
    category: 'provenance',
    expected: [
      'The answer is marked for review',
      'The invalid identifier is never shown as verified',
      'The inspector exposes the gate miss',
    ],
    implemented: true,
    requiredEventOrder: ['step', 'tool_call', 'tool_result', 'citations_ready', 'text_delta', 'turn_complete'],
    terminalEvent: 'turn_complete',
    diagnostic: { retrievalMode: 'nano', provenancePassed: false },
  },
  {
    id: 'source-manifest',
    title: 'Grouped source manifest',
    summary: 'A mixed-provenance manifest includes inspectable, matched, unverified, web, and unavailable sources.',
    category: 'provenance',
    expected: [
      'Source numbering and groups remain deterministic',
      'Every provenance class is explained in text',
      'Unavailable targets cannot masquerade as inspectable',
    ],
    implemented: true,
    requiredEventOrder: ['step', 'tool_call', 'tool_result', 'citations_ready', 'text_delta', 'turn_complete'],
    terminalEvent: 'turn_complete',
    diagnostic: { retrievalMode: 'nano', provenancePassed: false },
  },
  {
    id: 'rate-limited',
    title: 'Rate limit',
    summary: 'The backend rejects the request before streaming and restores the prompt for resubmission.',
    category: 'failure',
    expected: [
      'The failure is announced at the composer',
      'The prompt is restored for editing',
      'No partial assistant turn is invented',
    ],
    implemented: true,
    requiredEventOrder: [],
    terminalEvent: 'http_error',
    httpStatus: 429,
  },
  {
    id: 'very-long-answer',
    title: 'Very long answer',
    summary: 'Long headings, lists, citations, and paragraphs exercise reading width and overflow.',
    category: 'content',
    expected: [
      'No horizontal overflow',
      'Actions remain reachable',
      'Citation and prose layout stay stable',
    ],
    implemented: true,
    requiredEventOrder: ['step', 'tool_call', 'tool_result', 'citations_ready', 'text_delta', 'turn_complete'],
    terminalEvent: 'turn_complete',
    diagnostic: { retrievalMode: 'nano', provenancePassed: true },
  },
];

const scenarioIds = new Set<DemoScenarioId>(demoScenarios.map((scenario) => scenario.id));

export function isDemoScenarioId(value: string | null): value is DemoScenarioId {
  return value !== null && scenarioIds.has(value as DemoScenarioId);
}

export function captureDemoScenarioFromUrl(): DemoScenarioId {
  if (typeof window === 'undefined') return 'happy-path';
  try {
    const requested = new URLSearchParams(window.location.search).get('scenario');
    if (isDemoScenarioId(requested)) {
      window.sessionStorage.setItem(DEMO_SCENARIO_KEY, requested);
      return requested;
    }
    const stored = window.sessionStorage.getItem(DEMO_SCENARIO_KEY);
    return isDemoScenarioId(stored) ? stored : 'happy-path';
  } catch {
    return 'happy-path';
  }
}

export function getDemoScenario(): DemoScenarioId {
  return captureDemoScenarioFromUrl();
}

/**
 * Small, route-local variants for matter-entry evidence. These deliberately
 * do not expand the chat scenario catalog: they model the list boundary that
 * runs before a matter workspace can mount.
 */
export function getDemoMatterState(): DemoMatterState {
  if (typeof window === 'undefined') return 'ready';
  const value = new URLSearchParams(window.location.search).get('matterState');
  return value === 'empty'
    || value === 'unauthorized'
    || value === 'slow'
    || value === 'long'
    || value === 'load-failure'
    || value === 'create-failure'
    ? value
    : 'ready';
}

/**
 * Route-local claim-chart list/create variants. Keeping these separate from
 * the chat scenario catalog lets the structured-output routes exercise
 * deterministic lifecycle states without pretending they are SSE scenarios.
 */
export function getDemoClaimChartState(): DemoClaimChartState {
  if (typeof window === 'undefined') return 'ready';
  const value = new URLSearchParams(window.location.search).get('claimChartState');
  return value === 'empty'
    || value === 'unauthorized'
    || value === 'slow'
    || value === 'long'
    || value === 'load-failure'
    || value === 'create-failure'
    ? value
    : 'ready';
}

/**
 * Route-local claim-chart editor variants. These are intentionally separate
 * from the list/create fixture so an editor recovery case cannot alter the
 * list census that precedes it. The query is consumed only after the
 * development harness has passed `isDemoMode()`.
 */
export function getDemoClaimChartDetailState(): DemoClaimChartDetailState {
  if (typeof window === 'undefined') return 'ready';
  const value = new URLSearchParams(window.location.search).get(
    'claimChartDetailState',
  );
  return value === 'slow'
    || value === 'empty'
    || value === 'failed-once'
    || value === 'unauthorized'
    || value === 'mutation-failure'
    || value === 'generation-partial'
    || value === 'generation-slow'
    || value === 'long'
    ? value
    : 'ready';
}

/** Route-local deterministic tabular-review list/create variants. */
export function getDemoTabularReviewState(): DemoTabularReviewState {
  if (typeof window === 'undefined') return 'ready';
  const value = new URLSearchParams(window.location.search).get('tabularReviewState');
  return value === 'empty'
    || value === 'unauthorized'
    || value === 'slow'
    || value === 'long'
    || value === 'scope-mismatch'
    || value === 'load-failure'
    || value === 'create-failure'
    ? value
    : 'ready';
}

/** Development-only detail, mutation, and generation variants for W3.3. */
export function getDemoTabularReviewDetailState(): DemoTabularReviewDetailState {
  if (typeof window === 'undefined') return 'ready';
  const value = new URLSearchParams(window.location.search).get(
    'tabularReviewDetailState',
  );
  return value === 'slow'
    || value === 'empty'
    || value === 'failed-once'
    || value === 'unauthorized'
    || value === 'mutation-failure'
    || value === 'generation-partial'
    || value === 'generation-slow'
    || value === 'long'
    ? value
    : 'ready';
}

/**
 * Route-local deterministic document-upload behavior. It is read only by
 * the development harness after `isDemoMode()` has passed its repeated
 * `import.meta.env.DEV` guard, so production uploads can never select it.
 */
export function getDemoDocumentUploadState(): DemoDocumentUploadState {
  if (typeof window === 'undefined') return 'ready';
  const value = new URLSearchParams(window.location.search).get('uploadState');
  return value === 'audit' ? value : 'ready';
}

/** Development-only workflow library, mutation, and launch variants. */
export function getDemoWorkflowState(): DemoWorkflowState {
  if (typeof window === 'undefined') return 'ready';
  const value = new URLSearchParams(window.location.search).get('workflowState');
  return value === 'slow'
    || value === 'empty'
    || value === 'failed-once'
    || value === 'unauthorized'
    || value === 'stale'
    || value === 'mutation-failure'
    || value === 'mutation-unauthorized'
    || value === 'mutation-stale'
    || value === 'launch-failure'
    || value === 'launch-unauthorized'
    || value === 'launch-missing'
    || value === 'launch-partial'
    || value === 'launch-slow'
    || value === 'long'
    ? value
    : 'ready';
}
